import { types, flow, Instance, getEnv, getParent } from 'mobx-state-tree';
import { set, toJS } from 'mobx';
import { omit } from 'lodash';
import {
  AmazonPay,
  Analytics,
  Country,
  Labels,
  IState,
  Payments,
  PayPalExpress,
  Messages,
  SiteLimitation,
  StoreColors,
  StoreFeatures,
  StoreLogo,
  ISiteLimitation,
  ReCaptcha,
  StyleCustomizations,
} from '../sub-stores/Configuration';
import { RootServices } from '../../../types/RootStore';
import { SelectOption } from '../../../types/common';
import {
  ConfigEnabledPaymentMethodContract,
  PaymentMethod,
  CountryAndStateSelectOption,
  ConfigurationResponseData,
} from '../../../types/Configuration';
import { CountryResponse } from '../../../types/Country';
import {
  APIValues,
  DefaultValues,
  SiteLimitationKeys,
  SpreedlyValues,
  UIConstants,
} from '../../../utils/constants';
import PayPalService from '../../../services/PayPalService';
import { RootStoreInstance } from '../Root';

const CreditCardVirtualPaymentMethod = {
  id: '-1',
  method: 'Credit Card',
  methodType: APIValues.CREDIT_CARD_METHOD,
};

export const StateSelectionMessage: SelectOption = {
  label: UIConstants.STATE_DEFAULT_VALUE_MESSAGE,
  value: '',
};

const formatStateOptions = (options: SelectOption[]): SelectOption[] => {
  return options.length ? [StateSelectionMessage, ...options] : [];
};

export const Configuration = types
  .model({
    amazonPay: types.optional(AmazonPay, {}),
    analytics: types.optional(Analytics, {}),
    colors: types.optional(StoreColors, {}), // TODO: When feature is enabled for all merchants, remove `colors` and its model.
    styles: types.optional(StyleCustomizations, {}),
    countries: types.optional(types.array(Country), []),
    currencySymbol: types.optional(types.string, ''),
    currentServerDateTime: types.maybeNull(types.string),
    enabledState: types.optional(types.string, 'N'),
    features: types.optional(StoreFeatures, {}),
    labels: types.optional(Labels, {}),
    logo: types.optional(StoreLogo, {}),
    messages: types.optional(Messages, {}),
    payments: types.optional(Payments, {}),
    paypalExpress: types.optional(PayPalExpress, {}),
    isConfigured: types.optional(types.boolean, false),
    storeId: types.optional(types.string, ''),
    headerAccept: types.optional(types.string, ''),
    siteLimitations: types.array(SiteLimitation),
    reCaptcha: types.optional(ReCaptcha, {}),
  })
  .volatile(() => ({
    canDisplayPayPalButtons: false,
  }))
  .views(self => ({
    getSiteLimitation(key: string): ISiteLimitation | undefined {
      return self.siteLimitations.find(limitation => limitation.key === key);
    },
    isEnabled(key: string): boolean {
      return Boolean(this.getSiteLimitation(key)?.bool);
    },
  }))
  .views(self => ({
    get defaultCountry(): string {
      return DefaultValues.DEFAULT_SHIPPING_COUNTRY;
    },
    get formattedCurrencySymbol(): string {
      const storedSymbol =
        self.currencySymbol?.toLowerCase().replace(';', '') ?? '';
      switch (storedSymbol) {
        case '&euro':
        case '&#8364':
          return '€';
        case '&pound':
        case '&#163':
          return '£';
        case '&yen':
        case '&#165':
          return '¥';
        default:
          return self.currencySymbol;
      }
    },
    get enabledPaymentMethods(): ConfigEnabledPaymentMethodContract[] {
      return toJS(
        self.payments.enabledPaymentMethods
          .map(method => {
            return {
              ...method,
              methodType: method.methodType ?? method.method, // TODO: remove methodType property if found unnecessary by DB team
            };
          })
          .filter(method => method.methodType !== APIValues.ECHECK_METHOD)
          // Omit stripe payment if the feature is disabled.
          .filter(method => {
            return (
              method.methodType !== APIValues.STRIPE_METHOD ||
              (self.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
                method.methodType === APIValues.STRIPE_METHOD &&
                self.payments.stripeMerchantId)
            );
          })
      );
    },
    get hasStripePaymentMethodEnabled(): boolean {
      return !!this.enabledPaymentMethods.find(
        method => method.methodType === APIValues.STRIPE_METHOD
      );
    },
    getMatchingPaymentMethod(
      paymentMethodId: string
    ): ConfigEnabledPaymentMethodContract {
      return this.enabledPaymentMethods.find(
        method => method.id.toFixed(0) === paymentMethodId
      ) as ConfigEnabledPaymentMethodContract;
    },
    get enabledPaymentMethodsWithFormContext(): PaymentMethod[] {
      const displayedPaymentMethods = this.enabledPaymentMethods.reduce(
        (displayMethods, method) => {
          if (method.methodType !== APIValues.CREDIT_CARD_METHOD) {
            displayMethods.push({
              ...method,
              id: method.id.toFixed(0),
            });
          } else if (
            !displayMethods.some(
              existingMethod =>
                existingMethod.methodType === APIValues.CREDIT_CARD_METHOD
            )
          ) {
            displayMethods.push(CreditCardVirtualPaymentMethod);
          }

          return displayMethods;
        },
        [] as PaymentMethod[]
      );
      return displayedPaymentMethods;
    },
    get defaultPaymentMethodId(): string {
      if (self.paypalExpress.isDefaultPayment) {
        const paypalPaymentMethodId = PayPalService.findPayPalPaymentMethod(
          self.payments.enabledPaymentMethods
        )?.id.toFixed(0);

        if (paypalPaymentMethodId) return paypalPaymentMethodId;
      }

      return this.enabledPaymentMethodsWithFormContext[0]?.id ?? '';
    },
    get cardMethods(): ConfigEnabledPaymentMethodContract[] {
      return this.enabledPaymentMethods.filter(
        method => method.methodType === APIValues.CREDIT_CARD_METHOD
      );
    },
    get availableCountries(): string[] {
      return self.countries.map(country => country.Name);
    },
    get countriesAsSelectOptions(): SelectOption[] {
      return self.countries.map(country => {
        return { label: country.Name, value: country.Name };
      });
    },
    getCurrentStates(currentCountry: string): IState[] {
      const country = self.countries?.find(current => {
        return current.Name === currentCountry;
      });

      return toJS(country?.States) ?? [];
    },
    getCurrentStatesCodes(currentCountry: string): string[] {
      const country = self.countries?.find(current => {
        return current.Name === currentCountry;
      });

      const statesWithCodes = country?.States.filter(state => state.Code);
      return statesWithCodes?.map(state => state.Code as string) ?? [];
    },
    getCurrentStatesAsSelectOptions(currentCountry: string): SelectOption[] {
      const stateOptions = this.getCurrentStates(currentCountry).map(state => {
        return { label: state.Name, value: state.Code ?? state.Name };
      });
      return formatStateOptions(stateOptions);
    },
    getStateLabel(selectedCountry: string): string {
      const country = self.countries?.filter(current => {
        return current.Name === selectedCountry;
      });

      return country[0]?.StateLabel ?? 'State';
    },
    get countryAndStateOptions(): CountryAndStateSelectOption[] {
      return self.countries?.map(country => {
        const stateValues = country.States?.map(state => ({
          label: state.Name ?? '',
          value: state.Code ?? '',
        }));
        return {
          countryLabel: country.Name,
          countryValue: country.Name,
          stateLabel: country.StateLabel ?? 'State',
          stateValues: formatStateOptions(stateValues),
        };
      });
    },
  }))
  .actions(self => {
    const {
      addressAutocompleteService,
      configService,
      customerService,
      payPalService,
      spreedlyService,
      tokenizationService,
      windowService,
    } = getEnv<RootServices>(self);
    const { send, storeUrl } = getParent<RootStoreInstance>(self);
    const configurePayPal = async (): Promise<void> => {
      const isPayPalActive =
        !!self.paypalExpress.publicMerchantId &&
        Boolean(
          payPalService.findPayPalPaymentMethod(
            self.payments.enabledPaymentMethods
          )
        );
      set(self, { canDisplayPayPalButtons: isPayPalActive });
      if (
        isPayPalActive &&
        self.features.enablePayPalPpcp &&
        !self.payments.hasCardMethod
      ) {
        if (await payPalService.getIsAcpActive()) {
          const clientToken = await payPalService.getClientIdentityToken();
          self.paypalExpress.setAcpClientToken(clientToken);
        }
      } else {
        self.paypalExpress.disableCreditDebitCardLogos();
      }
    };

    const findCountryIsoCode = (countryName: string | null): string => {
      if (!countryName) return '';
      const countryMatch = self.countries.find(
        country => country.Name === countryName
      );
      return countryMatch?.Iso || '';
    };

    const findCountryNameByIsoCode = (isoCode: string): string => {
      if (!isoCode) return '';
      const countryMatch = self.countries.find(
        country => country.Iso === isoCode
      );
      return countryMatch?.Name || '';
    };

    const verifyReCaptchaFlow = flow(function* (reCaptchaValue: string) {
      return yield configService.verifyReCaptchaAsync(reCaptchaValue);
    });

    const getConfiguration = flow(function* () {
      try {
        send({ type: 'CONFIG_FETCHING', storeUrl });
        const configResponse: ConfigurationResponseData | null =
          yield configService.getConfigAsync();

        if (
          !configResponse ||
          !configResponse.features.checkoutAppIsAvailable
        ) {
          windowService.navigate('/one-page-checkout.asp');
          return;
        }

        self.styles.setStyles(configResponse.style);
        const configData = {
          ...omit(configResponse, 'style'),
          countries: [],
        };
        const countriesResponse = yield configService.getCountryConfigAsync();
        if (countriesResponse) {
          if (countriesResponse.length < 5) {
            const countryIsoCodes = (
              countriesResponse as CountryResponse[]
            ).map(({ Iso }) => Iso);
            const components = countryIsoCodes.map(
              isoCode => `country:${isoCode.toLowerCase()}`
            );
            const componentsString = components.join('|');
            addressAutocompleteService.setComponents(componentsString);
          }
          configData.countries = countriesResponse;
        }

        set(self, configData);

        yield configurePayPal();

        if (self.features.enableSpreedly) {
          spreedlyService.configure({
            scriptUrl: SpreedlyValues.SCRIPT_URL,
            environmentKey:
              configResponse.payments.spreedlyEnvironmentKey || '',
            cardNumberContainerId: SpreedlyValues.CARD_NUMBER_ID,
            cvvContainerId: SpreedlyValues.CVV_ID,
          });
          spreedlyService.loadScript();
        }

        if (!tokenizationService.isInitialized) {
          tokenizationService.initialize({
            apiUrl: configResponse.payments.pciUrl,
            pciMerchantId: configResponse.payments.pciMerchantId,
            enableStoredPaymentProfiles:
              configResponse.payments.enableStoredPaymentProfiles,
          });
        }
        set(self, { isConfigured: true });
      } catch (error) {
        // TODO: handle errors
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });

    const getEnabledPaymentMethodsFlow = flow(function* () {
      try {
        const result = yield customerService.getCustomerEnabledPaymentMethods();
        set(self.payments, { enabledPaymentMethods: result });
      } catch (error) {
        // TODO: add Dynatrace error logging
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
    return {
      findCountryIsoCode,
      findCountryNameByIsoCode,
      getConfiguration,
      getEnabledPaymentMethodsFlow,
      verifyReCaptchaFlow,
      afterAttach(): void {
        getConfiguration();
      },
    };
  });

export interface IConfiguration extends Instance<typeof Configuration> {}
